import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import {
  generateApplicationUrl,
  getQueryParamsFromHash,
  launchModal,
  parsePermittedQueryParams,
} from '@domain-group/javascript-utils'

import type { AppDispatch } from '@redux/store'

import * as trainingModuleActions from '@redux/modules/trainingModule'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import defaultFormState, { requiredFields } from '@models/trainingModule'
import type { TrainingModuleModel, TrainingModuleRequestOptions } from '@models/types'
import { loadTrainingCourse } from '@redux/modules/trainingCourse'

export const generateUrls = (trainingModule: Partial<TrainingModuleModel> = {}) => {
  const { id, training_course_id } = trainingModule

  // @ts-expect-error TS2339
  const { applicationUrl } = generateApplicationUrl({
    applicationKey: 'realhub',
    // @ts-expect-error TS2322
    environment: process.env.REACT_APP_APPLICATION_ENV,
  })

  return {
    editTrainingModuleUrl: `#/systemManager/trainingModules/${id}/Edit`,
    linkUrl: `${applicationUrl}/training/dashboard#/courses/${training_course_id}/modules/${id}`,
    trainingModulesIndexUrl: `#/systemManager/courses/${training_course_id}/edit`,
  }
}

type CreateTrainingModuleParams = {
  dispatch: AppDispatch,
  requestOptions?: TrainingModuleRequestOptions,
  trainingModuleParams: Partial<TrainingModuleModel>,
}

const createTrainingModule = (params: CreateTrainingModuleParams) => {
  const { trainingModuleParams, dispatch, requestOptions } = params

  const { createTrainingModule: createFn } = trainingModuleActions

  return dispatch(createFn(trainingModuleParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editTrainingModuleUrl } = generateUrls(data)

      return {
        data,
        redirectUrl: editTrainingModuleUrl,
        success,
      }
    }

    return response
  })
}

type UpdateTrainingModuleParams = {
  dispatch: AppDispatch,
  requestOptions?: TrainingModuleRequestOptions,
  trainingModule: TrainingModuleModel,
  trainingModuleParams: Partial<TrainingModuleModel>,
}

const updateTrainingModule = (params: UpdateTrainingModuleParams) => {
  const {
    trainingModule, trainingModuleParams, dispatch, requestOptions,
  } = params

  const { updateTrainingModule: updateFn } = trainingModuleActions

  const updatedParams = {
    id: trainingModule.id,
    ...trainingModuleParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DuplicateTrainingModuleParams = {
  dispatch: AppDispatch,
  requestOptions?: TrainingModuleRequestOptions,
  trainingModule: TrainingModuleModel,
  trainingModuleParams: Partial<TrainingModuleModel>,
}

const duplicateTrainingModule = (params: DuplicateTrainingModuleParams) => {
  const {
    trainingModule, trainingModuleParams, dispatch, requestOptions,
  } = params

  const { duplicateTrainingModule: duplicateFn } = trainingModuleActions

  const updatedParams = {
    id: trainingModule.id,
    ...trainingModuleParams,
  }

  // @ts-expect-error TS2345
  return dispatch(duplicateFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editTrainingModuleUrl } = generateUrls(data)

      return {
        data,
        redirectUrl: editTrainingModuleUrl,
        success,
      }
    }

    return response
  })
}

type DeleteTrainingModulePayload = {
  dispatch: AppDispatch,
  trainingModule: TrainingModuleModel,
}

const deleteTrainingModule = (params: DeleteTrainingModulePayload) => {
  const { trainingModule, dispatch } = params
  const { deleteTrainingModule: deleteFn } = trainingModuleActions

  return dispatch(deleteFn(trainingModule)).then((response) => {
    const { success } = response

    if (success){
      dispatch(
        loadTrainingCourse(trainingModule.training_course_id, {
          include_training_modules_count: true,
        }),
      )

      const { editTrainingModuleUrl } = generateUrls()
      const { redirect }: { redirect?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['redirect'])

      return {
        ...response,
        redirectUrl: redirect || editTrainingModuleUrl,
      }
    }

    return response
  })
}

export type ShowCreateOrDuplicateTrainingModule = {
  trainingModule: Partial<TrainingModuleModel>,
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useTrainingModuleForm(
  trainingModule: Partial<TrainingModuleModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const trainingModuleForm = useForm(
    defaultFormState,
    { entity: trainingModule, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [trainingModule.id, trainingModule.cache_key],
  )

  return {
    ...trainingModuleForm,
  }
}

export function useRelations(trainingModule: TrainingModuleModel) {
  const { training_course_id } = trainingModule || {}

  const trainingCourses = useSelector(reduxState => reduxState.entities.trainingCourses)

  const trainingCourse = trainingCourses[training_course_id] || {}

  return {
    trainingCourse,
  }
}

function useTrainingModule(initEntity: Partial<TrainingModuleModel> = {}) {
  // @ts-expect-error TS2345
  const { entity: trainingModule } = useLatestEntity(initEntity, 'trainingModules')

  const dispatch = useDispatch()

  // @ts-expect-error TS2345
  const { trainingCourse } = useRelations(trainingModule)
  const { callbacks } = useContext(PageContext)

  const {
    creating, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.trainingModules)

  const isLoaded = loadedIds.includes(trainingCourse.id)

  return {
    callbacks: {
      createOrDuplicateTrainingModule: () => launchModal({
        callbacks,
        modalKey: 'CreateOrDuplicateTrainingModuleModal',
        payload: { trainingModule },
      }),
      createTrainingModule: (
        trainingModuleParams: Partial<TrainingModuleModel>,
        requestOptions?: TrainingModuleRequestOptions,
      ) => createTrainingModule({ trainingModuleParams, dispatch, requestOptions }),
      duplicateTrainingModule: (
        trainingModuleParams: Partial<TrainingModuleModel>,
        requestOptions?: TrainingModuleRequestOptions,
      ) => duplicateTrainingModule({
        // @ts-expect-error TS2322
        trainingModule, trainingModuleParams, dispatch, requestOptions,
      }),
      // @ts-expect-error TS2322
      deleteTrainingModule: () => deleteTrainingModule({ trainingModule, dispatch }),
      updateTrainingModule: (
        trainingModuleParams: Partial<TrainingModuleModel>,
        requestOptions?: TrainingModuleRequestOptions,
      ) => updateTrainingModule({
        // @ts-expect-error TS2322
        trainingModule, trainingModuleParams, dispatch, requestOptions,
      }),
    },
    creating,
    loading: loading && !isLoaded,
    trainingCourse,
    trainingModule,
    updating,
    // @ts-expect-error TS2345
    urls: generateUrls(trainingModule),
  }
}

export default useTrainingModule
